import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import { RootState } from "@/store/store";
import { axiosApi, determiningNameAndVersionRobotById, getResponseError, getPostfixWithRobotId, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_SES, URL_SES } from "@/constants/api_endpoints";
import { IArchiveItem, IDialogDataResponse, IGetArchiveOfDialoguesProps, ISesArchiveState } from "@/types/ses/sesArchiveTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const dt = DateTime;

const initialState: ISesArchiveState = {
	listOfDialogues: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	dialogData: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	dialogAudio: {
		dataUrl: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		audioPeaks: [],
		audioPeakStatus: RequestStatus.IDLE,
		timestamp: 0,
	},
};

// архив диалогов
export const getArchiveOfDialogues = createAsyncThunk(
	"sesArchive/list",
	async (
		{ robotId, startDate, endDate, phone, channel, dataElemId, text, dialogueId, variable, hasUnknown }: IGetArchiveOfDialoguesProps,
		{ getState, rejectWithValue }
	) => {
		try {
			const response: AxiosResponse<IArchiveItem[] | IResponse> = await axiosApi.get(`${URL_SES}/${ENDPOINTS_SES.ARCHIVE_LIST}`, {
				params: {
					robot: robotId ? getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId) : undefined,
					startdate: startDate,
					enddate: endDate,
					phone,
					channel,
					data: dataElemId,
					text,
					uuid: dialogueId,
					variable,
					hasUnknown,
				},
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// данные диалога
export const getDialogData = createAsyncThunk(
	"sesArchive/data",
	async (uuid: string, { rejectWithValue, getState }) => {
		try {
			const response: AxiosResponse<IDialogDataResponse> = await axiosApi.get(`${URL_SES}/${ENDPOINTS_SES.ARCHIVE_DIALOG}/${uuid}`);
			const dictionaryDataElements = (getState() as RootState).sesDataElem.dataList.dictionary;
			const dictionaryEndpoints = (getState() as RootState).sesEndpoint.endpointList.dictionary;
			const robotList = (getState() as RootState).sesRobot.robotList.data;
			const dictionaryCategories = (getState() as RootState).qasCategory.categoriesList.dictionary;
			const translationDictionary = (getState() as RootState).lang.translate;
			return {
				responseData: response.data, // response
				dictionaryDataElements, // проброс словаря ЭД
				dictionaryEndpoints, // проброс словаря КТ
				robotList, // проброс списка роботов
				dictionaryCategories, // проброс словаря категорий
				translationDictionary, // проброс перевода
			};
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).sesArchive.dialogData.status === RequestStatus.LOADING) return false;
		},
	}
);

// аудио диалога
export const getDialogAudio = createAsyncThunk("sesArchive/audio", async (uuid: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.get(`${URL_SES}/${ENDPOINTS_SES.ARCHIVE_AUDIO}/${uuid}`, {
			responseType: "blob",
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение пиков аудио файла диалога
export const getAudioPeaks = createAsyncThunk("sesArchive/audioPeaks", async (uuid: string) => {
	const response: AxiosResponse<number[] | IResponse> = await axiosApi.get(`${URL_SES}/${ENDPOINTS_SES.ARCHIVE_WAVEFORM}/${uuid}`);
	return response.data;
});

const sesArchiveSlice = createSlice({
	name: "sesArchive",
	initialState,
	reducers: {
		// изменение временной метки
		changeTimestamp: (state, action: PayloadAction<number>) => {
			state.dialogAudio.timestamp = action.payload;
		},
		// очистка state
		clearState: (state) => {
			state.listOfDialogues = initialState.listOfDialogues;
			state.dialogData = initialState.dialogData;
			state.dialogAudio = initialState.dialogAudio;
		},
		// очистка списка диалогов
		clearListOfDialogues: (state) => {
			state.listOfDialogues = initialState.listOfDialogues;
		},
		// очистка данных диалога
		clearDialogData: (state) => {
			state.dialogData = initialState.dialogData;
		},
		// очистка аудио диалога
		clearDialogAudio: (state) => {
			state.dialogAudio = initialState.dialogAudio;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getArchiveOfDialogues.pending, (state) => {
				state.listOfDialogues.status = RequestStatus.LOADING;
			})
			.addCase(getArchiveOfDialogues.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.listOfDialogues.status = RequestStatus.IDLE;
					state.listOfDialogues.data = action.payload.reverse();
				} else state.listOfDialogues.status = RequestStatus.FAILED;
			})
			.addCase(getArchiveOfDialogues.rejected, (state, action: PayloadAction<unknown>) => {
				state.listOfDialogues.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.listOfDialogues.error = error;
				state.listOfDialogues.message = message;
			})
			.addCase(getDialogData.pending, (state) => {
				state.dialogData.status = RequestStatus.LOADING;
			})
			.addCase(getDialogData.fulfilled, (state, action) => {
				if (
					action.payload?.responseData &&
					typeof action.payload.responseData === "object" &&
					"dialog" in action.payload.responseData &&
					Array.isArray(action.payload.responseData.dialog)
				) {
					state.dialogData.status = RequestStatus.IDLE;
					let startDialog = 0; // начало даилога
					// let endDialog = 0; // конец диалога
					state.dialogData.data = action.payload.responseData.dialog.map((messageItem, idx, messageList) => {
						const datetimeRequest = messageItem.request.datetime || "";
						const datetimeReply = messageItem.reply.datetime || "";
						if (idx === 0) startDialog = dt.fromISO(datetimeRequest).toMillis();
						// else if (messageList.length - 1 === idx) endDialog = dt.fromISO(datetimeReply).toMillis() + 1;
						return {
							...messageItem,
							robot: determiningNameAndVersionRobotById({
								robotList: action.payload?.robotList || [],
								roboId: messageItem.robot,
								translationDictionary: action.payload?.translationDictionary || {},
							}), // id -> робот с версией
							endpoint: action.payload?.dictionaryEndpoints[messageItem.endpoint || ""] || messageItem.endpoint, // id -> КТ
							current: messageItem.current
								? {
										...messageItem.current,
										// id -> КТ
										id:
											messageItem.current.id && action.payload?.dictionaryEndpoints[messageItem.current.id]
												? action.payload.dictionaryEndpoints[messageItem.current.id]
												: messageItem.current.id,
										// id -> КТ
										nearest:
											messageItem.current.nearest && action.payload?.dictionaryEndpoints[messageItem.current.nearest]
												? action.payload.dictionaryEndpoints[messageItem.current.nearest]
												: messageItem.current.nearest,
										// id -> ЭД
										entry: Array.isArray(messageItem.current.entry)
											? messageItem.current.entry.map((dataElemId) => {
													return Array.isArray(action.payload?.dictionaryDataElements[dataElemId])
														? `${action.payload?.dictionaryDataElements[dataElemId][0]}, ${
																action.payload?.translationDictionary?.["title_weight"]?.toLowerCase() || "weight"
														  } ${action.payload?.dictionaryDataElements[dataElemId][1]}`
														: dataElemId;
											  })
											: messageItem.current.entry,
										// id -> ЭД
										missingRunData: Array.isArray(messageItem.current.missingRunData)
											? messageItem.current.missingRunData.map((dataElemId) => {
													return dataElemId && Array.isArray(action.payload?.dictionaryDataElements[dataElemId])
														? `${action.payload?.dictionaryDataElements[dataElemId][0]}, ${
																action.payload?.translationDictionary?.["title_weight"]?.toLowerCase() || "weight"
														  } ${action.payload?.dictionaryDataElements[dataElemId][1]}`
														: dataElemId;
											  })
											: messageItem.current.missingRunData,
										// id -> ЭД
										waitingRunData:
											messageItem.current.waitingRunData && Array.isArray(action.payload?.dictionaryDataElements[messageItem.current.waitingRunData])
												? `${action.payload?.dictionaryDataElements[messageItem.current.waitingRunData][0]}, ${
														action.payload?.translationDictionary?.["title_weight"]?.toLowerCase() || "weight"
												  } ${action.payload?.dictionaryDataElements[messageItem.current.waitingRunData][1]}`
												: messageItem.current.waitingRunData,
										// id -> категории
										category: Array.isArray(messageItem.current.category)
											? messageItem.current.category.map((categoryId) => action.payload?.dictionaryCategories[categoryId] || categoryId)
											: messageItem.current.category,
								  }
								: undefined,
							startRequest: dt.fromISO(datetimeRequest).toMillis() - startDialog + (idx === 0 ? 1 : 0), // смещение на 1мс для стартового сообщения
							endRequest: dt.fromISO(datetimeReply).toMillis() - 1 - startDialog,
							startReply: dt.fromISO(datetimeReply).toMillis() - startDialog,
							endReply:
								messageList.length - 1 === idx
									? dt.fromISO(datetimeReply).toMillis() + 1 - startDialog
									: dt.fromISO(messageList[idx + 1]?.request.datetime || "").toMillis() - 1 - startDialog,
						};
					});
				} else state.dialogData.status = RequestStatus.FAILED;
			})
			.addCase(getDialogData.rejected, (state, action: PayloadAction<unknown>) => {
				state.dialogData.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.dialogData.error = error;
				state.dialogData.message = message;
			})
			.addCase(getDialogAudio.pending, (state) => {
				state.dialogAudio.status = RequestStatus.LOADING;
			})
			.addCase(getDialogAudio.fulfilled, (state, action) => {
				if (action.payload instanceof Blob) {
					state.dialogAudio.status = RequestStatus.IDLE;
					const url = window.URL.createObjectURL(action.payload);
					state.dialogAudio.dataUrl = url;
				} else state.dialogAudio.status = RequestStatus.FAILED;
			})
			.addCase(getDialogAudio.rejected, (state, action: PayloadAction<unknown>) => {
				state.dialogAudio.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.dialogAudio.error = error;
				state.dialogAudio.message = message;
			})
			.addCase(getAudioPeaks.pending, (state) => {
				state.dialogAudio.audioPeakStatus = RequestStatus.LOADING;
			})
			.addCase(getAudioPeaks.fulfilled, (state, action) => {
				if (action.payload && Array.isArray(action.payload)) {
					state.dialogAudio.audioPeakStatus = RequestStatus.IDLE;
					state.dialogAudio.audioPeaks = action.payload;
				} else state.dialogAudio.audioPeakStatus = RequestStatus.FAILED;
			})
			.addCase(getAudioPeaks.rejected, (state) => {
				state.dialogAudio.audioPeakStatus = RequestStatus.FAILED;
				console.error("Peak loading error. Peaks will be built on the client side.");
			});
	},
});

export const { changeTimestamp, clearState, clearListOfDialogues, clearDialogData, clearDialogAudio } = sesArchiveSlice.actions;

export const selectArchiveList = (state: RootState) => state.sesArchive.listOfDialogues;
export const selectDialogData = (state: RootState) => state.sesArchive.dialogData;
export const selectDialogAudio = (state: RootState) => state.sesArchive.dialogAudio;

export default sesArchiveSlice.reducer;
